<template>
  <v-layout
    align-center
    class="spot-detail-toolbar"
  >
    <v-layout
      :align-center="$vuetify.breakpoint.smAndUp"
      wrap
    >
      <!-- Date with Refresh Button -->
      <div v-if="permissions.refreshDate">
        <v-layout align-center>
          <div class="grey--text text--darken-1">
            {{ spot.date | dayjs('DD.MM.YYYY') }}
          </div>
          <v-btn
            :disabled="loading"
            icon
            small
            class="ml-1 mr-3"
            :title="$t('refreshContentDate')"
            @click="$emit('refresh-date')"
          >
            <v-icon color="msr">
              refresh
            </v-icon>
          </v-btn>
        </v-layout>
      </div>
      <!-- Author -->
      <v-layout
        v-if="!!spot.author"
        align-center
      >
        <img
          v-if="!!spot.author && !!spot.author.image"
          :src="spot.author.image"
          class="mr-3"
          style="object-fit: contain"
          :style="{
            'max-height': spot.author.is_group ? '72px' : '48px',
            'max-width': spot.author.is_group ? '144px' : '96px',
            'border-radius': spot.author.is_group ? 0 : '50%',
          }"
        >
        <v-text-field
          disabled
          class="v-text-custom-disabled"
          :label="$t('author')"
          hide-details
          :value="spot.author.label"
        />
      </v-layout>
      <v-spacer />
      <!-- Current Tags -->
      <div v-if="spot.id && permissions.tag">
        <tag-select-list
          :tags="spot.tags"
          :removable="permissions.tag"
          removable-tooltip="removeTagFromSpot"
          :xs="$vuetify.breakpoint.xs"
          @remove="removeTag"
        />
      </div>
    </v-layout>
    <!-- Assign/remove Tags -->
    <tag-select
      v-if="permissions.tag"
      :disabled="loading"
      :tag-entries-can-be-deactivated="spot.tag_entries_can_be_deactivated"
      :tags="compatibleUserTagsNotYetAddedToSpot"
      add-tooltip="addTagToSpot"
      medium
      @add="onTagToAddSelected"
    />
    <!-- Share -->
    <v-menu
      v-if="showShareOnSocialMediaButton"
      offset-y
      bottom
      right
      :disabled="loading"
    >
      <v-badge
        slot="activator"
        overlap
        color="transparent"
      >
        <span
          slot="badge"
          :class="{
            'beta-feature-marker': $store.state.common.showBetaFeatureMarkers,
          }"
        />
        <v-btn
          :disabled="loading"
          class="ma-0"
          color="grey--text text--darken-1 hoverable-icon"
          icon
          :loading="shareOnSocialMediaButtonLoading"
          :title="$t('shareContentOnSocialMedia')"
          @click="$store.dispatch('RETRIEVE_IS_USING_WEBVIEW')"
        >
          <v-icon>share</v-icon>
        </v-btn>
      </v-badge>
      <v-list
        :dense="$vuetify.breakpoint.xs"
        class="list-tile-auto-height"
      >
        <!-- Share to Facebook Page -->
        <span
          :title="
            disableShareOnFacebookButton
              ? disableShareOnFacebookButtonMessage
              : $t('shareVideoOnFacebook')
          "
        >
          <v-list-tile
            :disabled="
              disableShareOnFacebookButton &&
                disableShareOnFacebookButtonHandler == null
            "
            @click="
              disableShareOnFacebookButton
                ? disableShareOnFacebookButtonHandler()
                : $emit('share-on-facebook-page')
            "
          >
            <v-list-tile-avatar>
              <v-img
                :src="
                  disableShareOnFacebookButton
                    ? '/static/icons8-facebook-96-gray.png'
                    : '/static/icons8-facebook-96.png'
                "
              />
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('shareVideoOnFacebook') }}
              </v-list-tile-title>
              <v-list-tile-sub-title
                v-if="disableShareOnFacebookButton"
                class="msr--text wrap"
              >
                {{ disableShareOnFacebookButtonMessage }}
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
        </span>
        <v-divider />
        <!-- Share to Instagram Feed -->
        <span
          :title="
            disableShareOnInstagramButton
              ? disableShareOnInstagramButtonMessage
              : $t('shareVideoOnInstagramFeed')
          "
        >
          <v-list-tile
            :disabled="disableShareOnInstagramButton"
            @click="$emit('share-on-instagram')"
          >
            <v-list-tile-avatar>
              <v-img
                :src="
                  disableShareOnInstagramButton
                    ? '/static/instagram-96-gray.png'
                    : '/static/instagram-96.png'
                "
              />
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>{{
                $t('shareVideoOnInstagramFeed')
              }}</v-list-tile-title>
              <v-list-tile-sub-title
                v-if="disableShareOnInstagramButton"
                class="msr--text wrap"
              >
                {{ disableShareOnInstagramButtonMessage }}
              </v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action>
              <v-btn
                class="ma-0 grey--text"
                icon
                @click.stop="showInstagramHelpDialog()"
              >
                <v-icon>help_outline</v-icon>
              </v-btn>
            </v-list-tile-action>
          </v-list-tile>
        </span>
      </v-list>
    </v-menu>
    <!-- Statistics -->
    <v-btn
      v-if="showStatisticsButton"
      class="ma-0"
      color="grey--text text--darken-1"
      :disabled="loading"
      icon
      data-cy="spot-store-statistics-btn"
      :title="$t('showContentStoreUsageStatistics')"
      :to="hrefContentStoreStatisticsPage"
    >
      <v-icon
        class="hoverable-icon"
        medium
      >
        bar_chart
      </v-icon>
    </v-btn>
    <!-- Content Management Actions (if User is contentmanager) -->
    <v-menu
      v-if="showSettingsButton"
      v-model="menu"
      offset-y
      :disabled="loading"
      left
      :close-on-content-click="false"
    >
      <v-btn
        slot="activator"
        color="grey--text text--darken-1"
        icon
        :disabled="loading"
        class="ma-0"
      >
        <v-icon
          class="hoverable-icon"
          medium
          data-cy="content-actions-activator"
        >
          settings
        </v-icon>
      </v-btn>
      <v-list class="pb-0">
        <v-subheader>{{ $t('contentManagementSubheaderGeneral') }}</v-subheader>
        <!-- Rename -->
        <v-list-tile
          v-if="permissions.rename"
          @click="$emit('rename')"
        >
          <v-list-tile-avatar>
            <v-icon color="msr">
              edit
            </v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('renameContent') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <!-- Categorize -->
        <v-list-tile
          class="list-tile-auto-height"
          :disabled="loading"
        >
          <v-list-tile-avatar>
            <v-icon color="msr">
              folder
            </v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-select
              :loading="loading"
              :label="$t('changeCategory')"
              :items="categories"
              :value="spot.category ? spot.category.id : null"
              item-text="name"
              item-value="id"
              hide-details
              data-cy="change-category-select"
              @change="$emit('categorize', $event)"
            >
              <template
                slot="item"
                slot-scope="data"
              >
                <v-list-tile-content>
                  <v-list-tile-title>{{ data.item.name }}</v-list-tile-title>
                </v-list-tile-content>
                <small
                  v-if="data.item.is_default"
                  class="grey--text ml-3"
                >{{
                  $t('default')
                }}</small>
                <small
                  v-else-if="data.item.group"
                  class="grey--text ml-3"
                >{{
                  data.item.group.name
                }}</small>
              </template>
            </v-select>
          </v-list-tile-content>
        </v-list-tile>
        <!-- Refresh Date -->
        <v-list-tile
          v-if="permissions.refreshDate"
          @click="$emit('refresh-date')"
        >
          <v-list-tile-avatar>
            <v-icon color="msr">
              refresh
            </v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              {{ $t('refreshContentDate') }}
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <!-- Replace -->
        <v-list-tile
          v-if="permissions.replace"
          :disabled="bundle && bundle.has_multiple_formats"
          @click="$emit('replace')"
        >
          <v-list-tile-avatar>
            <v-icon
              :color="bundle && bundle.has_multiple_formats ? 'grey' : 'msr'"
            >
              swap_horiz
            </v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('replaceContent') }}</v-list-tile-title>
            <v-list-tile-sub-title
              v-if="bundle && bundle.has_multiple_formats"
              class="warning--text"
            >
              {{ $t('replaceContentHint_disbandFirst') }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
        <!-- Download -->
        <v-list-tile
          v-if="permissions.download"
          @click="$emit('download')"
        >
          <v-list-tile-avatar>
            <v-icon color="msr">
              download
            </v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('downloadContent') }}</v-list-tile-title>
            <v-list-tile-sub-title v-if="!!previewedSpot">
              {{ previewedSpot.tvformat.name }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-divider />
        <template v-if="permissions.tag && spot.tags">
          <v-subheader>
            {{ $t('contentManagementSubheaderTaggingSpecific') }}
          </v-subheader>
          <!-- Allow deactivating Tag-Entries -->
          <v-list-tile
            :key="updating && spot.tag_entries_can_be_deactivated"
            :disabled="updating"
            :title="$t('contentUploadTagEntriesCanBeDeactivatedTooltip')"
            @click="$emit('toggle-tag-entries-can-be-deactivated')"
          >
            <v-list-tile-avatar>
              <v-progress-circular
                v-if="updating"
                :size="20"
                :width="2"
                indeterminate
                class="msr--text"
              />
              <template v-else>
                <v-icon
                  v-if="spot.tag_entries_can_be_deactivated"
                  color="msr"
                >
                  lock_open
                </v-icon>
                <v-icon
                  v-else
                  color="warning"
                >
                  lock
                </v-icon>
              </template>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                <span v-if="spot.tag_entries_can_be_deactivated">{{
                  $t('contentManagementDisableTagEntriesCanBeDeactivated')
                }}</span>
                <span v-else>{{
                  $t('contentManagementEnableTagEntriesCanBeDeactivated')
                }}</span>
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider />
        </template>
        <template
          v-if="
            (permissions.editTemplateData &&
              spot.template &&
              spot.template_data) ||
              (permissions.changeImageDuration && !!spot.uploadimage) ||
              permissions.disbandTVSpotBundle
          "
        >
          <v-subheader>
            {{ $t('contentManagementSubheaderFormatSpecific') }}
          </v-subheader>
          <!-- Edit Template Data -->
          <v-list-tile
            v-if="
              permissions.editTemplateData &&
                spot.template &&
                spot.template_data
            "
            @click="$emit('edittemplatedata')"
          >
            <v-list-tile-avatar>
              <v-icon color="msr">
                web
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('tvtemplatesEditTemplateDataMenuOption') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <!-- Change Image Duration -->
          <v-list-tile
            v-if="permissions.changeImageDuration && !!spot.uploadimage"
            @click="$emit('change-image-duration')"
          >
            <v-list-tile-avatar>
              <v-icon color="msr">
                av_timer
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('changeImageDuration') }}
              </v-list-tile-title>
              <v-list-tile-sub-title>
                {{ $t('currently') }}: {{ spot.duration }}s
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <!-- Disband TVSpotBundle -->
          <v-list-tile
            v-if="permissions.disbandTVSpotBundle"
            @click="
              $emit('disband-bundle');
              menu = false;
            "
          >
            <v-list-tile-avatar>
              <multiformat-bundle-indicator is-multiformat-bundle />
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('disbandTVSpotBundle') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider />
        </template>
        <template v-if="permissions.unpublish || permissions.editHardRange">
          <v-subheader>
            {{ $t('contentManagementSubheaderClearance') }}
          </v-subheader>
          <!-- Publish -->
          <v-list-tile
            v-if="!spot.published && permissions.unpublish"
            @click="$emit('publish')"
          >
            <v-list-tile-avatar>
              <v-icon color="msr">
                visibility
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('publishContentNow') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <!-- Unpublish -->
          <v-list-tile
            v-if="spot.published && permissions.unpublish"
            data-cy="unpublish-btn"
            @click="$emit('unpublish')"
          >
            <v-list-tile-avatar>
              <v-icon color="msr">
                visibility_off
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('unpublishContent') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <!-- Edit Hard Range -->
          <v-list-tile
            v-if="permissions.editHardRange"
            data-cy="edit-hard-range-tile"
            @click="$emit('edithardrange')"
          >
            <v-list-tile-avatar>
              <v-icon color="msr">
                date_range
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('contentHardRangeSet') }}
              </v-list-tile-title>
              <v-list-tile-sub-title>
                <span v-if="spot.hard_start">
                  {{ $t('from') }}
                  {{ spot.hard_start | dayjs('DD.MM.YYYY') }}
                </span>
                <span v-if="spot.hard_expire">
                  {{ $t('to').toLowerCase() }}
                  {{
                    subtractOneDayFromDate(spot.hard_expire)
                      | dayjs('DD.MM.YYYY')
                  }}
                </span>
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>
        <!-- Delete -->
        <v-divider v-if="permissions.delete" />
        <v-hover v-if="permissions.delete">
          <v-list-tile
            slot-scope="{hover}"
            class="pb-1"
            :class="[hover ? 'error' : '']"
            @click="$emit('delete')"
          >
            <v-list-tile-avatar>
              <v-icon :color="hover ? 'white' : 'msr'">
                delete
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title :class="[hover ? 'white--text' : '']">
                {{ $t('deleteContent') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-hover>
      </v-list>
    </v-menu>
    <!-- Batch Actions Menu -->
    <v-slide-x-reverse-transition>
      <spot-detail-batch-menu
        v-if="showBatchMenu"
        :spot="spot"
        :entries="entries"
        @timing="$emit('entry-selection-overlay-toggle', 'timing')"
        @hotrotating="$emit('entry-selection-overlay-toggle', 'hotrotating')"
        @overlayediting="
          $emit('entry-selection-overlay-toggle', 'overlayediting')
        "
        @removing="$emit('entry-selection-overlay-toggle', 'removing')"
      />
    </v-slide-x-reverse-transition>
  </v-layout>
</template>

<script>
import {addTagToSpot, removeTagFromSpot} from '@/mixins/tags';
import {CONNECT_FACEBOOK_TO_INSTAGRAM_HELP_PAGE_URL} from '@/mixins/shareOnSocialMedia';
import timing from '@/mixins/timing';

import MultiformatBundleIndicator from '@/components/MultiformatBundleIndicator';
import SpotDetailBatchMenu from '@/components/SpotDetail/SpotDetailBatchMenu';
import TagSelectList from '@/components/TagSelectList';
import TagSelect from '@/components/TagSelect';

export default {
  components: {
    MultiformatBundleIndicator,
    SpotDetailBatchMenu,
    TagSelectList,
    TagSelect,
  },
  mixins: [timing],
  props: {
    spot: {
      type: Object,
      required: true,
    },
    bundle: {
      type: Object,
      default: null,
    },
    entries: {
      type: Array[Object],
      required: true,
    },
    previewedSpot: {
      type: Object,
      default: null,
    },
    settingsMenu: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    updating: {
      type: Boolean,
      default: false,
    },
    /**
     * Assumed to be already filtered for the Spot context.
     */
    categories: {
      type: Array[Object],
      required: true,
    },
    showStatisticsButton: {
      type: Boolean,
      required: true,
    },
    showShareOnSocialMediaButton: {
      type: Boolean,
      default: false,
    },
    disableShareOnFacebookButton: {
      type: Boolean,
      default: false,
    },
    disableShareOnFacebookButtonMessage: {
      type: String,
      required: true,
    },
    disableShareOnFacebookButtonHandler: {
      type: Function,
      required: true,
    },
    disableShareOnInstagramButton: {
      type: Boolean,
      default: true,
    },
    disableShareOnInstagramButtonMessage: {
      type: String,
      required: true,
    },
    showSettingsButton: {
      type: Boolean,
      required: true,
    },
    showBatchMenu: {
      type: Boolean,
      required: true,
    },
    permissions: {
      type: Object,
      required: true,
    },
    shareOnSocialMediaButtonLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasExistingNonTagSpotEntries() {
      return (
        !!this.entries &&
        this.entries.filter(
          (entry) => (entry.through_matching_tags || []).length === 0
        ).length > 0
      );
    },
    compatibleUserTagsNotYetAddedToSpot() {
      if (!this.$userprofile) {
        return [];
      }

      return this.$store.state.common.tags.filter((tag) => {
        // Tags and the thing we want to use them on must be in the same Group.
        const matchingGroup = this.spot.group.id === tag.group;
        if (!matchingGroup) {
          return false;
        }

        // Ignore Tag if already assigned.
        for (const spotTag of this.spot.tags) {
          if (tag.id === spotTag.id) {
            return false;
          }
        }

        return true;
      });
    },
    menu: {
      get() {
        return this.settingsMenu;
      },
      set(menu) {
        this.$emit('update:settingsMenu', menu);
      },
    },
    hrefContentStoreStatisticsPage() {
      const content = this.bundle || this.spot;
      return `/spot/${content.ctype}/${content.id}/stats`;
    },
  },
  methods: {
    showInstagramHelpDialog() {
      const title = this.$t('shareToInstagramHelpDialogTitle');
      const content = `
        <div class="subheading">
          <p>${this.$t('shareToInstagramHelpDialogText_1')}</p>
          <p>${this.$t('shareToInstagramHelpDialogText_2')}:</p>
        </div>
        <a
          href="${CONNECT_FACEBOOK_TO_INSTAGRAM_HELP_PAGE_URL}"
          target="_blank"
          class="font-weight-bold subheading"
        >
          ${this.$t('shareToInstagramHelpDialogText_3')}
        </a>
      `;
      this.$bus.$emit('show-dynamic-dialog', {
        title: title,
        content: content,
      });
    },
    onTagToAddSelected(tag) {
      if (this.hasExistingNonTagSpotEntries) {
        this.askUserToConfirmEntriesRemovedOnTagAssignment(tag);
        return;
      }
      this.addTag(tag);
    },
    askUserToConfirmEntriesRemovedOnTagAssignment(tag) {
      const vm = this;
      const title = this.$t('tagAssignmentRemovesSpotEntriesDialogTitle');
      const hint1 = this.$t('tagAssignmentRemovesSpotEntriesDialogHint_1');
      const hint2 = this.$t('tagAssignmentRemovesSpotEntriesDialogHint_2');
      this.$bus.$emit('show-dynamic-dialog', {
        title: title,
        titleIcon: 'tag',
        content: `
					<div class="subheading">
						${hint1}
						<div class="error--text mt-2 pa-2">
							<strong>${hint2}</strong>
						</div>
					</div>
				`,
        onConfirm() {
          vm.addTag(tag, vm.spot);
          vm.$bus.$emit('hide-dynamic-dialog');
        },
      });
    },
    addTag(tag) {
      return addTagToSpot(tag, this.spot)
        .then(() => {
          this.$emit('tags-changed');
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        });
    },
    removeTag(tag) {
      return removeTagFromSpot(tag, this.spot)
        .then(() => {
          this.$emit('tags-changed');
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        });
    },
  },
};
</script>

<style></style>

<style scoped>
.spot-detail-toolbar > *:not(:last-child) {
  margin-right: 8px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/SpotDetail/SpotDetailToolbar.vue